import React, { useState, useRef } from 'react';
import { Redirect } from 'react-router-dom';

import { useSelector } from 'react-redux';
import { Grid, TextField, Button, Typography, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import useFormField, { Form, validateForm } from 'common/hooks/useFormField';
import Container from 'common/components/Container';
import Spinner from 'common/components/Spinner';

import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import ContainerBox from 'components/ContainerBox';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as UserSelectors from 'selectors/UserSelectors';

import { Routes, push } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';
import { CATEGORY_MAX_DESCRIPTION_CHARS } from 'common/constants/categories';
import { newFirestoreId } from 'common/utils/dataMigrations';
import { maxBy } from 'lodash';
import { api } from 'common/frontend/api';
import { Category } from 'common/types';

type NewCategoryForm = {
	name: string;
	description: string;
};

const CreateCategory = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const nameFieldRef = useRef<HTMLInputElement>(null);
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);

	const shop = useSelector(ShopSelectors.activeShopData)!;
	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const categories = useSelector(StockSelectors.categoriesData);
	const isAdmin = useSelector(UserSelectors.isAdmin);

	const validateName = (name: string) => {
		if (!name || name.length < 1) {
			return t('common:validation.cannotBeEmpty', {
				field: t('common:form.name', 'Name'),
				defaultValue: '{{field}} cannot be empty',
			});
		}

		if (name.length > CATEGORY_MAX_DESCRIPTION_CHARS) {
			return t('common:validation.lengthAtMost', {
				productName: t('common:form.name', 'Name'),
				int: '200',
				defaultValue: '{{productName}} can be at most {{int}} characters',
			});
		}

		return;
	};

	const validateDescription = (description: string) => {
		if (description && description.length > CATEGORY_MAX_DESCRIPTION_CHARS) {
			return t('common:validation.lengthAtMost', {
				productName: t('common:form.description', 'Description'),
				int: '200',
				defaultValue: '{{productName}} can be at most {{int}} characters',
			});
		}

		return;
	};

	const form: Form<NewCategoryForm> = {
		name: useFormField<string>('', validateName),
		description: useFormField<string>('', validateDescription),
	};

	const createCategory = async ({ name, description }: { name: string; description: string }) => {
		const shopId = shop.publicInfo.shopId;
		const categoryId = newFirestoreId();
		const newOrderIndex = maxBy(categories ?? [], 'orderIndex')?.orderIndex ?? 1;
		const newCategory: Category = {
			id: categoryId,
			shopId,
			name: {
				def: name,
				[defaultLanguage]: name,
			},
			description: {
				def: description ?? '',
				[defaultLanguage]: description ?? '',
			},
			imageUrl: null,
			terms: null,
			orderIndex: newOrderIndex,
			bookingBuffer: {
				daysBefore: 0,
				minutesBefore: 0,
			},
		};
		await api().productCategories.doc(categoryId).set(newCategory);
		return newCategory;
	};

	const handleSubmit = async () => {
		const { errors, values } = validateForm(form);
		if (errors.length > 0) return;
		setLoading(true);

		try {
			if (!shop) {
				throw new Error(t('common:errors.error', 'Error occured. Please try again later.'));
			}
			const category = await createCategory({ name: values.name, description: values.description });
			push(Routes.CategoryPage(category.id));
		} catch (e) {
			setError(e.message);
		}
		setLoading(false);
	};

	if (!isAdmin) {
		return <Redirect to={Routes.CatalogTab('categories')} />;
	}

	return (
		<>
			<SetPageTitle title={t('category.newCategory', 'New category')} />
			<SetPageNavAction
				backAction={{
					to: Routes.CatalogTab('categories'),
					title: t('category.categories', 'Categories'),
				}}
			/>
			<Container densePaddingTop>
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextField
								inputRef={nameFieldRef}
								autoFocus
								autoComplete="off"
								fullWidth
								id="name"
								label={t('category.categoryName', 'Category name')}
								name={'name'}
								value={form.name.value}
								onChange={form.name.handleChange}
								margin="normal"
								type="text"
								error={Boolean(form.name.error)}
								helperText={form.name.error}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={5}
								variant="outlined"
								label={t('category.categoryDescription', 'Category description')}
								value={form.description.value}
								onChange={form.description.handleChange}
								margin="normal"
								type="text"
								error={Boolean(form.description.error)}
								helperText={form.description.error}
								inputProps={{
									'data-qa': 'categoryDescription',
								}}
							/>
						</Grid>
						<Grid item xs={12} direction="column" alignItems="center">
							<Box className={classes.bottom}>
								{Boolean(error) && (
									<Typography variant="body1" color="error" align="center" gutterBottom>
										{t('common:errors.error', 'Error occured. Please try again later')}
									</Typography>
								)}
								<Button
									fullWidth
									disabled={loading}
									onClick={handleSubmit}
									variant="contained"
									color="primary"
									data-qa="createCategory"
								>
									{loading ? (
										<Spinner padding={8} thickness={4} color="white" />
									) : (
										t('category.createCategory', 'Create category')
									)}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</ContainerBox>
			</Container>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		bottom: {
			margin: '0 auto',
			width: '100%',
			maxWidth: '300px',
		},
	}),
);

export default CreateCategory;
